/**
 * Dónde los formularios y las páginas generadas dibujan lo que el proyecto
 * registró en sus asientos:
 *  - FieldAdornmentSlot: el adorno de un campo (fieldAdornments.tsx), al lado
 *    del control, en el alta, la edición y la vista.
 *  - ViewExtrasSlot: el agregado de la vista de un registro (viewExtras.tsx),
 *    arriba del primer grupo.
 *  - CreatedExtrasSlot y hasCreatedExtras: lo que se muestra después de guardar
 *    un alta (createdExtras.tsx).
 * Sin nada registrado para la entidad no dibujan nada propio: la página queda
 * igual que sin asientos. Los formularios, la vista y la página de alta los
 * montan solo en las entidades con .projectSlots en el DSL (y en sus
 * proyecciones). Este archivo es del generador: el proyecto no lo llena.
 */
import React from 'react';
import { Box } from '@mui/material';
import { fieldAdornments } from './fieldAdornments';
import type { FieldAdornmentProps } from './fieldAdornments';
import { viewExtras } from './viewExtras';
import type { ViewExtrasProps } from './viewExtras';
import { createdExtras } from './createdExtras';
import type { CreatedExtrasProps } from './createdExtras';

/** Lo que el proyecto registró para la entidad: solo las claves propias del asiento (constructor o toString no son una entidad). */
const registeredFor = <T,>(seat: Partial<Record<string, T>>, entity: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(seat, entity) ? seat[entity] : undefined;

/**
 * El campo y su adorno, en una fila que no envuelve: el adorno queda al costado
 * del campo, nunca debajo. Los controles de los formularios y de la vista tienen
 * su propio ancho (500 por defecto), así que la fila mide lo que el campo más el
 * adorno, sin pasar el ancho disponible: el campo ocupa lo que queda y el adorno
 * no se achica. Un adorno que no dibuja nada para un campo no ocupa lugar y el
 * campo queda con su ancho. Un campo propio (components/custom/) que ocupa todo
 * el ancho (fullWidth) queda, en una entidad con adorno, con su ancho natural.
 * En pantallas de menos de unos 540 px el control de 500 ya se desborda sin
 * adorno; con adorno, el campo se angosta (minWidth: 0), el control sigue
 * desbordándose y el adorno queda pintado encima de su borde derecho.
 */
export const FieldAdornmentSlot: React.FC<FieldAdornmentProps & { children?: React.ReactNode }> = ({
  children,
  ...props
}) => {
  const Adornment = registeredFor(fieldAdornments, props.entity);
  if (!Adornment) return <>{children}</>;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', width: 'fit-content', maxWidth: '100%' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      <Box sx={{ flex: 'none' }}>
        <Adornment {...props} />
      </Box>
    </Box>
  );
};

export const ViewExtrasSlot: React.FC<ViewExtrasProps> = (props) => {
  const Extras = registeredFor(viewExtras, props.entity);
  return Extras ? <Extras {...props} /> : null;
};

/** Hay algo para mostrar después de guardar un alta de esta entidad. */
export const hasCreatedExtras = (entity: string): boolean => !!registeredFor(createdExtras, entity);

export const CreatedExtrasSlot: React.FC<CreatedExtrasProps> = (props) => {
  const Extras = registeredFor(createdExtras, props.entity);
  return Extras ? <Extras {...props} /> : null;
};
